import { colors } from '@/components/landing/landingTokens';

/**
 * Shared design tokens for the teacher adaptive-question workspaces (homework
 * builder, worksheet builder) — DESIGN.md §4.
 *
 * Deliberately distinct from the student Playground homework theme
 * (`src/components/Homework/homeworkTheme.ts`). The result grid uses the
 * load-bearing green/red roles for correct/wrong; the builder uses cobalt for
 * selection and orange for the brand publish/freeze action.
 */
export const C = {
  cobalt: '#3A56D4',
  cobaltDark: '#2A3EB1',
  /** 8% cobalt selection wash — mirrors the DAG selected-node fill. */
  cobaltWash: 'rgba(58, 86, 212, 0.08)',
  green: '#2EA366',
  greenDark: '#1E7A4A',
  brick: '#D63847',
  /** Destructive button bottom shadow + hover (DESIGN.md §4 Brick-dark). */
  brickDark: '#9B2832',
  orange: '#F06830',
  orangeDark: '#B84A1C',
  orangeLight: '#FF8A5C',
  ink: '#1A1E30',
  inkMuted: '#6A7088',
  border: '#E2E6F2',
  /** Base page surface (DESIGN.md §4 Background). */
  bg: '#F5F7FC',
  bgAlt: '#EDF0F9',
  /** Card and panel surfaces (DESIGN.md §4 Paper, `--paper`). */
  paper: colors.paper,
  /**
   * White labels and icons on a cobalt, green or brick fill; on brand orange
   * only at 19px/800 or larger (DESIGN.md §10).
   */
  onAccent: colors.paper,
} as const;

/**
 * Pure-CSS scroll-edge shadows for a vertical scrollbox: a soft ink shadow
 * appears at the top/bottom edge only while more content lies that way;
 * "cover" gradients matching the box's backdrop hide the shadows at the ends
 * of the scroll range. `background-attachment: local` does the state
 * tracking — no JS. Shadow hue matches the DESIGN.md §7 shadow system.
 */
export const scrollShadowsSx = (backdrop: string) => ({
  background: [
    `linear-gradient(${backdrop} 30%, rgba(255, 255, 255, 0)) center top`,
    `linear-gradient(rgba(255, 255, 255, 0), ${backdrop} 70%) center bottom`,
    'radial-gradient(farthest-side at 50% 0, rgba(12, 18, 45, 0.12), rgba(12, 18, 45, 0)) center top',
    'radial-gradient(farthest-side at 50% 100%, rgba(12, 18, 45, 0.12), rgba(12, 18, 45, 0)) center bottom',
  ].join(', '),
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 36px, 100% 36px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
});

/**
 * A row control's hit area (DESIGN.md §8 Touch targets): 40px under a mouse,
 * 48px on a touch screen. It is drawn over a `footprint`-px slot, so a dense
 * row keeps its height and columns: the negative margin gives the extra size
 * back to the layout, and the icon stays centred where it was.
 */
export const rowControlTargetSx = (footprint: number) => ({
  width: 40,
  height: 40,
  m: `${(footprint - 40) / 2}px`,
  '@media (pointer: coarse)': {
    width: 48,
    height: 48,
    m: `${(footprint - 48) / 2}px`,
  },
});
